import { useState, useEffect } from 'react';
import { Collapsible } from '../../components/Collapsible';
import { uploadTimelineComment, upsertFileToDiskFolder, findSubfolderByPrefix } from '../deal-context/bx24';
import { generateAllFiles } from './generateXlsx';
import { generateCuttingPdf } from './generatePdf';
import type { MaterialResult } from '../cutting-calc/CalcResultView';
import type { ReportSettings } from '../cutting-calc/useReportSettings';
import type { ParsedSpec } from '../solidworks-parser';
import type { NcReferenceMap } from '../reference-data/types';
import type { SheetGroupResult } from '../sheet-calc';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  results: MaterialResult[];
  spec: ParsedSpec;
  blade: number;
  trim: number;
  dealId: string;
  parentFolderId: number | null;
  settings: ReportSettings;
  ncRef: NcReferenceMap | null;
  sheetResults?: SheetGroupResult[];
}

// ─── Types ────────────────────────────────────────────────────────────────────

type PdfOrientation = 'portrait' | 'landscape';

type UploadState =
  | { kind: 'idle' }
  | { kind: 'generating' }
  | { kind: 'uploading'; step: string }
  | { kind: 'done'; commentId: number; diskUrls: string[] }
  | { kind: 'error'; message: string };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function downloadFile(bytes: Uint8Array, name: string, mime: string) {
  const blob = new Blob([bytes as unknown as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const PDF_MIME  = 'application/pdf';
const BORDER = '1px solid var(--color-track)';
const PDF_ORI_KEY = 'linecut:pdf-orientation';

function DownloadBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 14px', fontSize: 13, fontWeight: 500,
        border: BORDER, borderRadius: 6, background: 'var(--color-white)',
        color: 'var(--color-black)', cursor: 'pointer',
        transition: 'background 120ms',
      }}
      onMouseOver={(e) => (e.currentTarget.style.background = 'var(--color-bg)')}
      onMouseOut={(e) => (e.currentTarget.style.background = 'var(--color-white)')}
    >
      <span style={{ fontSize: 15 }}>↓</span>
      {label}
    </button>
  );
}

// ─── ExportPanel ─────────────────────────────────────────────────────────────

export function ExportPanel({ results, spec, blade, trim, dealId, parentFolderId, settings, ncRef, sheetResults }: Props) {
  const [uploadState, setUploadState] = useState<UploadState>({ kind: 'idle' });
  const [pdfOrientation, setPdfOrientation] = useState<PdfOrientation>(
    () => (localStorage.getItem(PDF_ORI_KEY) as PdfOrientation | null) ?? 'portrait',
  );

  useEffect(() => {
    localStorage.setItem(PDF_ORI_KEY, pdfOrientation);
  }, [pdfOrientation]);

  async function pushFilesToDisk(folderId: number): Promise<string[]> {
    const files = generateAllFiles(results, spec, blade, trim, dealId, ncRef, sheetResults);
    const allFiles = [files.purchase, files.production, files.onec];

    const calcFolderId = await findSubfolderByPrefix(folderId, '3.');
    if (calcFolderId === null) throw new Error('Папка «3. Расчет» не найдена в папке сделки');

    const urls: string[] = [];
    for (const f of allFiles) {
      const uploaded = await upsertFileToDiskFolder(calcFolderId, f.name, f.base64);
      urls.push(uploaded.DETAIL_URL);
    }
    return urls;
  }

  function handleDownload(fileKey: 'purchase' | 'production' | 'onec') {
    const files = generateAllFiles(results, spec, blade, trim, dealId, ncRef, sheetResults);
    const f = files[fileKey];
    downloadFile(f.bytes, f.name, XLSX_MIME);
  }

  async function handleUploadToBitrix() {
    setUploadState({ kind: 'generating' });

    let xlsxFiles;
    let pdfFile;
    try {
      xlsxFiles = generateAllFiles(results, spec, blade, trim, dealId, ncRef, sheetResults);
      pdfFile   = await generateCuttingPdf(results, blade, trim, settings, pdfOrientation);
    } catch (e) {
      setUploadState({ kind: 'error', message: `Ошибка генерации: ${e instanceof Error ? e.message : String(e)}` });
      return;
    }

    const allXlsx = [xlsxFiles.purchase, xlsxFiles.production, xlsxFiles.onec];

    // Step 1: Timeline comment — xlsx + PDF
    setUploadState({ kind: 'uploading', step: 'Публикую комментарий в таймлайне...' });
    let commentId: number;
    try {
      const comment =
        `Раскрой посчитан. Файлы приложены.\n` +
        `Лезвие: ${blade} мм, торцовка: ${trim} мм.`;
      commentId = await uploadTimelineComment(
        dealId,
        comment,
        [...allXlsx, pdfFile].map((f) => ({ name: f.name, base64: f.base64 })),
      );
    } catch (e) {
      setUploadState({ kind: 'error', message: `Ошибка публикации в таймлайн: ${e instanceof Error ? e.message : String(e)}` });
      return;
    }

    const diskUrls: string[] = [];
    if (parentFolderId !== null) {
      // Step 2: xlsx → "3. Расчет"
      setUploadState({ kind: 'uploading', step: 'Загружаю xlsx в папку «3. Расчет»...' });
      try {
        diskUrls.push(...await pushFilesToDisk(parentFolderId));
      } catch (e) {
        console.warn('[ExportPanel] xlsx disk upload failed:', e);
      }

      // Step 3: PDF → "2.КД"
      setUploadState({ kind: 'uploading', step: 'Загружаю схему раскроя в папку «2.КД»...' });
      try {
        const kdFolderId = await findSubfolderByPrefix(parentFolderId, '2.');
        if (kdFolderId !== null) {
          const up = await upsertFileToDiskFolder(kdFolderId, pdfFile.name, pdfFile.base64);
          diskUrls.push(up.DETAIL_URL);
        } else {
          console.warn('[ExportPanel] subfolder "2.*" not found inside parent folder', parentFolderId);
        }
      } catch (e) {
        console.warn('[ExportPanel] PDF disk upload failed:', e);
      }
    }

    setUploadState({ kind: 'done', commentId, diskUrls });
  }

  return (
    <Collapsible title="Выходные файлы" defaultOpen={true}>
      <div style={{ padding: '16px' }}>

        {/* Download buttons */}
        <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--color-gray)' }}>
          Скачать файл на компьютер:
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <DownloadBtn label="Задание на закупку" onClick={() => handleDownload('purchase')} />
          <DownloadBtn label="Задание на производство" onClick={() => handleDownload('production')} />
          <DownloadBtn label="Таблица для загрузки в 1С" onClick={() => handleDownload('onec')} />
          <DownloadBtn label="Схема раскроя (PDF)" onClick={() => void (async () => {
            try {
              const f = await generateCuttingPdf(results, blade, trim, settings, pdfOrientation);
              downloadFile(f.bytes, f.name, PDF_MIME);
            } catch (e) { console.error('[ExportPanel] PDF download failed:', e); }
          })()} />
        </div>

        {/* PDF orientation toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <span style={{ fontSize: 12, color: 'var(--color-gray)' }}>Ориентация PDF:</span>
          <button
            onClick={() => setPdfOrientation((o) => o === 'portrait' ? 'landscape' : 'portrait')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 10px', fontSize: 12, fontWeight: 500,
              border: BORDER, borderRadius: 5, background: 'var(--color-white)',
              color: 'var(--color-black)', cursor: 'pointer',
            }}
          >
            {pdfOrientation === 'portrait' ? '↕ Книжная' : '↔ Альбомная'}
          </button>
        </div>

        {/* Bitrix upload */}
        <div style={{ borderTop: BORDER, paddingTop: 16 }}>
          {uploadState.kind === 'idle' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={() => void handleUploadToBitrix()}
                style={{
                  padding: '8px 20px', fontSize: 13, fontWeight: 600,
                  background: 'var(--color-red)', color: '#fff',
                  border: 'none', borderRadius: 6, cursor: 'pointer',
                  transition: 'background 120ms',
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = '#b02220')}
                onMouseOut={(e) => (e.currentTarget.style.background = 'var(--color-red)')}
              >
                Загрузить в Bitrix
              </button>
              <span style={{ fontSize: 12, color: 'var(--color-gray)' }}>
                xlsx + PDF схема в таймлайн
                {parentFolderId !== null && '  ·  xlsx → «3. Расчет»  ·  PDF → «2.КД»'}
              </span>
            </div>
          )}

          {(uploadState.kind === 'generating' || uploadState.kind === 'uploading') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Spinner />
              <span style={{ fontSize: 13, color: 'var(--color-gray)' }}>
                {uploadState.kind === 'generating'
                  ? 'Генерирую файлы...'
                  : uploadState.step}
              </span>
            </div>
          )}

          {uploadState.kind === 'done' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--color-in-stock)', fontWeight: 600 }}>
                ✓ Файлы загружены в таймлайн сделки (комментарий #{uploadState.commentId})
              </span>
              <button
                onClick={() => setUploadState({ kind: 'idle' })}
                style={{ fontSize: 12, color: 'var(--color-gray)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', width: 'fit-content' }}
              >
                Загрузить снова
              </button>
            </div>
          )}

          {uploadState.kind === 'error' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--color-red)', fontWeight: 600 }}>
                ✗ {uploadState.message}
              </span>
              <button
                onClick={() => setUploadState({ kind: 'idle' })}
                style={{ fontSize: 12, color: 'var(--color-gray)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', width: 'fit-content' }}
              >
                Попробовать снова
              </button>
            </div>
          )}
        </div>

      </div>
    </Collapsible>
  );
}

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" style={{ animation: 'spin 1s linear infinite' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <circle cx="8" cy="8" r="6" fill="none" stroke="var(--color-track)" strokeWidth="2" />
      <path d="M8 2 A6 6 0 0 1 14 8" fill="none" stroke="var(--color-red)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
